// ── OperationsManual — 오픈·영업·마감 매뉴얼 (V86) ─────────
// 매장 운영 SOP. 한식 음식점 기본값 + 사용자 수정 가능.
// Firestore users/{uid}.sopChecklist 필드에 저장.
// 부모님 친화: 큰 글씨, 단순 체크리스트, 인쇄 시 □ 체크박스 출력.

import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../db';
import { SOP_DEFAULT, SOP_DEFAULT_ZH, SOP_SECTIONS } from '../data/sopDefaults';
import { useLang } from '../i18n/LangContext';   // V147 중국어 화면

const SECTION_TITLES = {
  open:   { num: 1, label: '오픈',   desc: '영업 시작 전 준비' },
  during: { num: 2, label: '영업 중', desc: '영업 시간 동안 반복하는 일' },
  close:  { num: 3, label: '마감',   desc: '영업 종료 후 정리' },
};
// V147: 중국어 화면 — 섹션 이름, 그리고 매뉴얼 자체도 중국어 매뉴얼(sopChecklist_zh)을 따로 쓴다
const SECTION_TITLES_ZH = {
  open:   { num: 1, label: '开店',   desc: '营业前的准备' },
  during: { num: 2, label: '营业中', desc: '营业时间里反复做的事' },
  close:  { num: 3, label: '关店',   desc: '营业结束后的整理' },
};
function useZ() {
  const { lang } = useLang();
  const zh = lang === 'zh';
  return { zh, Z: (ko, z) => (zh ? z : ko) };
}

const bigInput = {
  width: '100%', padding: '10px 12px',
  fontSize: 18, fontWeight: 600,
  color: '#18181B', background: '#fff',
  border: '2px solid #A1A1AA', borderRadius: 6,
  outline: 'none', boxSizing: 'border-box',
};

export default function OperationsManual({ user, ownerUid, onBack, storeName }) {
  const { zh, Z } = useZ();
  // V147: 매니저(직원)도 매장(사장님) 문서의 매뉴얼을 본다. 중국어 화면은 중국어 매뉴얼을 따로.
  const docUid = ownerUid || user?.uid;
  const FIELD = zh ? 'sopChecklist_zh' : 'sopChecklist';
  const DEFAULT = zh ? SOP_DEFAULT_ZH : SOP_DEFAULT;
  const TITLES = zh ? SECTION_TITLES_ZH : SECTION_TITLES;
  const [sop, setSop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const dragIdxRef = useRef(null);

  // ── Firestore 로드 ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!docUid) {
        if (!cancelled) { setSop(DEFAULT); setLoading(false); }
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', docUid));
        const stored = snap.exists() ? snap.data()[FIELD] : null;
        if (cancelled) return;
        // 저장된 데이터가 있으면 사용, 없으면 default. 일부 섹션만 있으면 default로 보완.
        if (stored && typeof stored === 'object') {
          const merged = {};
          SOP_SECTIONS.forEach(k => {
            merged[k] = stored[k] || DEFAULT[k];
          });
          setSop(merged);
        } else {
          setSop(DEFAULT);
        }
      } catch (e) {
        console.warn('SOP load failed:', e?.message);
        if (!cancelled) setSop(DEFAULT);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docUid, zh]);

  // ── 저장 ───────────────────────────────────────────────
  const persistSop = async (next) => {
    setSop(next);
    if (!docUid) return;
    try {
      await setDoc(doc(db, 'users', docUid), { [FIELD]: next }, { merge: true });
    } catch (e) {
      console.warn('SOP save failed:', e?.message);
      alert(Z('저장에 실패했어요. 인터넷 연결을 확인해 주세요.', '保存失败，请检查网络。'));
    }
  };

  // ── 편집 모드 진입/종료 ────────────────────────────────
  const startEdit = (key) => {
    if (!sop) return;
    setEditingSection(key);
    setEditDraft({
      label: sop[key].label,
      items: sop[key].items.map(it => ({ ...it })),
    });
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditDraft(null);
  };

  const commitEdit = async () => {
    if (!editingSection || !editDraft) return;
    const cleaned = {
      label: editDraft.label || sop[editingSection].label,
      items: editDraft.items
        .filter(it => it.title && it.title.trim())
        .map(it => ({
          id: it.id || `${editingSection}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          title: it.title.trim(),
          time: (it.time || '').trim(),
          note: (it.note || '').trim(),
        })),
    };
    await persistSop({ ...sop, [editingSection]: cleaned });
    cancelEdit();
  };

  // ── 항목 편집 헬퍼 ─────────────────────────────────────
  const updateItem = (idx, field, value) => {
    setEditDraft(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, [field]: value } : it),
    }));
  };
  const addItem = () => {
    setEditDraft(prev => ({
      ...prev,
      items: [...prev.items, { id: `new_${Date.now()}`, title: '', time: '', note: '' }],
    }));
  };
  const removeItem = (idx) => {
    setEditDraft(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };
  const moveItem = (from, to) => {
    setEditDraft(prev => {
      if (to < 0 || to >= prev.items.length) return prev;
      const next = [...prev.items];
      const [r] = next.splice(from, 1);
      next.splice(to, 0, r);
      return { ...prev, items: next };
    });
  };
  const onDragStart = (idx) => { dragIdxRef.current = idx; };
  const onDragOver  = (e)   => e.preventDefault();
  const onDrop = (idx) => {
    const from = dragIdxRef.current;
    if (from == null || from === idx) return;
    moveItem(from, idx);
    dragIdxRef.current = null;
  };

  // ── 렌더 ───────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontSize: 18, color: '#71717A' }}>
        {Z('매뉴얼 불러오는 중...', '正在加载流程...')}
      </div>
    );
  }

  return (
    <div>
      <TopBar title={Z('오픈·영업·마감 매뉴얼', '开店 · 营业 · 关店流程')} onBack={onBack} hidePrint={!!editingSection} />
      <div className="manual-page" style={{
        maxWidth: 880, margin: '0 auto', padding: '24px 20px',
        fontSize: 18, color: '#18181B', lineHeight: 1.6,
      }}>
        <PrintHeader title={Z('매장 운영 매뉴얼', '门店工作流程')} storeName={storeName} />

        <div className="no-print" style={{
          background: '#FAFAFA', border: '1px solid #E4E4E7', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20,
          fontSize: 16, color: '#52525B', lineHeight: 1.6,
        }}>
          {zh ? <>已经放好了韩国餐厅的标准流程。可以用 [✏️ 修改] 按门店情况添加 · 删除 · 调整顺序。<br />点 [打印] 会打印成员工可以在 □ 里打勾的表格。</>
              : <>한식 음식점 표준 매뉴얼이 기본으로 들어 있어요.
          매장에 맞게 [✏️ 수정] 버튼으로 항목을 추가·삭제·순서 변경할 수 있어요.
          [종이로 인쇄] 누르면 직원이 □에 표시하며 따라할 수 있는 형태로 출력됩니다.</>}
        </div>

        {SOP_SECTIONS.map(key => {
          const section = sop[key];
          const meta = TITLES[key];
          const isEditingThis = editingSection === key;

          if (isEditingThis) {
            return (
              <EditingSection
                key={key}
                meta={meta}
                draft={editDraft}
                onLabel={(v) => setEditDraft(prev => ({ ...prev, label: v }))}
                onItem={updateItem}
                onAdd={addItem}
                onRemove={removeItem}
                onMove={moveItem}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onSave={commitEdit}
                onCancel={cancelEdit}
              />
            );
          }
          return (
            <ReadOnlySection
              key={key}
              meta={meta}
              section={section}
              onEdit={() => startEdit(key)}
              disabled={!!editingSection}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── TopBar / PrintHeader (ManualPage와 일관 ─ 별도 컴포넌트화) ──
function TopBar({ title, onBack, hidePrint }) {
  const { Z } = useZ();
  return (
    <div className="no-print" style={{
      position: 'sticky', top: 56, zIndex: 90, background: '#fff',
      borderBottom: '2px solid #18181B', padding: '14px 20px',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <button onClick={onBack}
        style={{
          padding: '12px 20px', fontSize: 18, fontWeight: 700,
          background: '#fff', color: '#18181B',
          border: '2px solid #18181B', borderRadius: 8, cursor: 'pointer',
        }}>
        {Z('← 돌아가기', '← 返回')}
      </button>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#18181B', flex: 1, minWidth: 120 }}>
        {title}
      </div>
      {!hidePrint && (
        <button onClick={() => window.print()}
          style={{
            padding: '14px 28px', fontSize: 20, fontWeight: 800,
            background: '#18181B', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}>
          {Z('종이로 인쇄', '打印')}
        </button>
      )}
    </div>
  );
}

function PrintHeader({ title, storeName }) {
  const { zh } = useZ();
  const today = (() => {
    const d = new Date();
    return zh ? `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日` : `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
  })();
  return (
    <div style={{ borderBottom: '3px solid #18181B', paddingBottom: 14, marginBottom: 22 }}>
      <div style={{ fontSize: 32, fontWeight: 800, color: '#18181B', marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, color: '#52525B' }}>
        {storeName && <>{storeName} &middot; </>}
        {zh ? '打印日期：' : '인쇄일: '}{today}
      </div>
    </div>
  );
}

// ── 읽기 모드 섹션 ─────────────────────────────────────────
function ReadOnlySection({ meta, section, onEdit, disabled }) {
  const { Z } = useZ();
  return (
    <div className="sop-section" style={{
      marginBottom: 30, paddingBottom: 18,
      breakInside: 'avoid', pageBreakInside: 'avoid',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 26, fontWeight: 800, color: '#18181B',
            borderBottom: '2px solid #18181B', paddingBottom: 6,
          }}>
            {meta.num}. {meta.label} <span style={{ fontSize: 17, fontWeight: 600, color: '#52525B' }}>— {section.label}</span>
          </div>
          <div style={{ fontSize: 15, color: '#71717A', marginTop: 4 }}>{meta.desc}</div>
        </div>
        <button onClick={onEdit} disabled={disabled} className="no-print"
          style={{
            padding: '8px 16px', fontSize: 16, fontWeight: 700,
            background: '#fff', color: '#18181B',
            border: '2px solid #18181B', borderRadius: 6,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.3 : 1,
          }}>
          ✏️ {Z('수정', '修改')}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
        <thead>
          <tr style={{ background: '#FAFAFA' }}>
            <th style={{ ...thStyle, width: 50, textAlign: 'center' }}>{Z('확인', '确认')}</th>
            <th style={{ ...thStyle, width: 90 }}>{Z('시간', '时间')}</th>
            <th style={thStyle}>{Z('할 일', '要做的事')}</th>
            <th style={{ ...thStyle, width: '32%' }}>{Z('메모', '备注')}</th>
          </tr>
        </thead>
        <tbody>
          {section.items.map((it, i) => (
            <tr key={it.id || i}>
              <td style={{ ...tdStyle, textAlign: 'center', fontSize: 26, lineHeight: 1 }}>□</td>
              <td style={{ ...tdStyle, color: '#52525B', whiteSpace: 'nowrap' }}>{it.time || '—'}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{it.title}</td>
              <td style={{ ...tdStyle, color: '#52525B' }}>{it.note || '—'}</td>
            </tr>
          ))}
          {section.items.length === 0 && (
            <tr>
              <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#71717A', padding: '24px 14px' }}>
                {Z('항목이 없어요. [✏️ 수정] 버튼으로 추가해 주세요.', '还没有内容。请点 [✏️ 修改] 添加。')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const thStyle = {
  border: '1.5px solid #18181B', padding: '10px 12px',
  textAlign: 'left', fontWeight: 800, fontSize: 17, color: '#18181B',
};
const tdStyle = {
  border: '1.5px solid #A1A1AA', padding: '10px 12px', fontSize: 17, verticalAlign: 'top',
};

// ── 편집 모드 섹션 ─────────────────────────────────────────
function EditingSection({
  meta, draft,
  onLabel, onItem, onAdd, onRemove, onMove,
  onDragStart, onDragOver, onDrop,
  onSave, onCancel,
}) {
  const { Z } = useZ();
  if (!draft) return null;
  return (
    <div className="no-print sop-section" style={{
      marginBottom: 30, padding: 18,
      border: '3px solid #18181B', borderRadius: 10, background: '#FAFAFA',
    }}>
      <div style={{
        fontSize: 22, fontWeight: 800, color: '#18181B', marginBottom: 14,
      }}>
        {meta.num}. {meta.label} {Z('수정 중', '修改中')}
      </div>

      <Label>{Z('섹션 제목 (인쇄 시 헤더로 나옴)', '标题（打印时显示在上面）')}</Label>
      <input value={draft.label || ''}
        onChange={e => onLabel(e.target.value)}
        style={{ ...bigInput, marginBottom: 14 }}
        placeholder={Z('예) 오픈 (영업 시작 1시간 전)', '例：开店（营业前 1 小时）')} />

      <Label>{Z('할 일 목록', '要做的事')}</Label>
      <div style={{ fontSize: 14, color: '#71717A', marginBottom: 8 }}>
        {Z('손잡이(≡)를 잡고 끌어 순서를 바꾸거나, 오른쪽 ▲▼ 버튼을 눌러 옮길 수 있어요.', '按住 ≡ 拖动，或点右边的 ▲▼ 调整顺序。')}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
        <thead>
          <tr style={{ background: '#fff' }}>
            <th style={{ ...thStyle, width: 44, textAlign: 'center' }}>{Z('순서', '顺序')}</th>
            <th style={{ ...thStyle, width: '14%' }}>{Z('시간', '时间')}</th>
            <th style={thStyle}>{Z('할 일', '要做的事')}</th>
            <th style={{ ...thStyle, width: '30%' }}>{Z('메모', '备注')}</th>
            <th style={{ ...thStyle, width: 100, textAlign: 'center' }}>{Z('이동', '移动')}</th>
            <th style={{ ...thStyle, width: 60, textAlign: 'center' }}>{Z('삭제', '删除')}</th>
          </tr>
        </thead>
        <tbody>
          {draft.items.map((it, idx) => (
            <tr key={it.id || idx}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              style={{ background: '#fff' }}>
              <td style={{ ...tdEdit, textAlign: 'center', cursor: 'grab',
                           fontSize: 22, color: '#71717A', userSelect: 'none' }}
                  title={Z('끌어서 순서 변경', '拖动调整顺序')}>≡</td>
              <td style={tdEdit}>
                <input value={it.time || ''}
                  onChange={e => onItem(idx, 'time', e.target.value)}
                  style={bigInput} placeholder={Z('예) T-30분', '例：T-30分')} />
              </td>
              <td style={tdEdit}>
                <input value={it.title || ''}
                  onChange={e => onItem(idx, 'title', e.target.value)}
                  style={bigInput} placeholder={Z('예) 밥솥 취사 시작', '例：电饭锅开始煮饭')} />
              </td>
              <td style={tdEdit}>
                <input value={it.note || ''}
                  onChange={e => onItem(idx, 'note', e.target.value)}
                  style={bigInput} placeholder={Z('부연 설명 (선택)', '补充说明（可选）')} />
              </td>
              <td style={{ ...tdEdit, textAlign: 'center' }}>
                <button onClick={() => onMove(idx, idx - 1)}
                  disabled={idx === 0}
                  style={{ ...iconBtn, opacity: idx === 0 ? 0.3 : 1 }} title={Z('위로', '上移')}>▲</button>
                <button onClick={() => onMove(idx, idx + 1)}
                  disabled={idx === (draft.items.length - 1)}
                  style={{ ...iconBtn, opacity: idx === (draft.items.length - 1) ? 0.3 : 1 }} title={Z('아래로', '下移')}>▼</button>
              </td>
              <td style={{ ...tdEdit, textAlign: 'center' }}>
                <button onClick={() => onRemove(idx)}
                  style={{ ...iconBtn, color: '#92400E' }} title={Z('이 줄 삭제', '删除这一行')}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button onClick={onAdd}
        style={{
          marginTop: 10, padding: '12px 20px', fontSize: 17, fontWeight: 700,
          background: '#fff', color: '#18181B',
          border: '2px dashed #18181B', borderRadius: 8, cursor: 'pointer',
          width: '100%',
        }}>
        {Z('+ 항목 한 줄 추가', '+ 添加一行')}
      </button>

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button onClick={onCancel}
          style={{
            flex: 1, padding: '14px 20px', fontSize: 18, fontWeight: 700,
            background: '#fff', color: '#18181B',
            border: '2px solid #A1A1AA', borderRadius: 8, cursor: 'pointer',
          }}>
          {Z('취소', '取消')}
        </button>
        <button onClick={onSave}
          style={{
            flex: 2, padding: '14px 20px', fontSize: 18, fontWeight: 800,
            background: '#18181B', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
          {Z('저장하기', '保存')}
        </button>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 15, fontWeight: 700, color: '#52525B', marginBottom: 4 }}>
      {children}
    </div>
  );
}

const iconBtn = {
  padding: '6px 10px', margin: '0 2px', fontSize: 18, fontWeight: 700,
  background: '#fff', color: '#18181B',
  border: '2px solid #A1A1AA', borderRadius: 6, cursor: 'pointer',
  minWidth: 38,
};
const tdEdit = { border: '1.5px solid #A1A1AA', padding: '6px 8px', verticalAlign: 'middle' };
